// ./src/ActionSheet/ResponsiveMenu.tsx
//
// A button's menu: Mantine's dropdown Menu on a desktop, the bottom ActionSheet on a phone (big rows under the thumb
// instead of a small floating box). The SAME sections — or the same custom content — drive both, so they cannot drift.
//
// ⚠ A click inside the menu stays inside it. The dropdown and the sheet are PORTALS, and React bubbles a portal's
//   events to its React parents: a menu on a guest card opened the guest's drawer as well as running the item (the
//   Guests page stopped it by hand). Both stop it here.
// ⓘ Nothing to offer → nothing drawn (a ⋯ that opened an empty box).
// ⓘ It can be CONTROLLED (`opened` / `onOpenedChange`): a ticket card's swipe action opens it with no button, and
//   typing `/help` in a composer opens the help.

import React, { useState } from 'react';
import { Menu, MenuProps, Text } from '@mantine/core';

import { useAppEnvironment } from '../AppEnvironmentContext';
import { AppIcon } from '../AppIcon';

import { ActionSheet, ActionSheetSection } from './ActionSheet';

export interface ResponsiveMenuProps {
  /** The control that opens the menu. On a phone its click opens the sheet. */
  target: React.ReactElement<{ onClick?: React.MouseEventHandler }>;
  /** The phone sheet's title — say what the menu is for ("Edit", "Set RSVP status"). */
  title: React.ReactNode;
  sections?: ActionSheetSection[];
  /** Content that is not a list of rows (a reaction palette). Given `close`, to close the menu after a pick. */
  children?: (close: () => void) => React.ReactNode;
  /** The desktop dropdown's position (default bottom-end), width and the height it scrolls past. */
  position?: MenuProps['position'];
  width?: MenuProps['width'];
  maxHeight?: number | string;
  /** Controlled open state (omit to let the menu manage it). */
  opened?: boolean;
  onOpenedChange?: (opened: boolean) => void;
  /** Desktop-only Menu settings (e.g. `trapFocus: false` so typing continues in a composer). */
  menuProps?: Partial<MenuProps>;
}

const stop = (e: React.SyntheticEvent) => e.stopPropagation();

export const ResponsiveMenu: React.FC<ResponsiveMenuProps> = ({
  target,
  title,
  sections = [],
  children,
  position = 'bottom-end',
  width,
  maxHeight,
  opened,
  onOpenedChange,
  menuProps,
}) => {
  const { isMobile } = useAppEnvironment();
  const [ownOpen, setOwnOpen] = useState(false);
  const open = opened ?? ownOpen;
  const setOpen = (next: boolean) => {
    if (opened === undefined) {
      setOwnOpen(next);
    }

    onOpenedChange?.(next);
  };
  const shown = sections.filter((s) => s.items.length > 0);
  const close = () => setOpen(false);

  if (shown.length === 0 && !children) {
    return null;
  }

  if (isMobile) {
    return (
      <>
        {React.cloneElement(target, {
          onClick: (e: React.MouseEvent) => {
            target.props.onClick?.(e);
            setOpen(true);
          },
        })}
        <span onClick={stop} style={{ display: 'contents' }}>
          <ActionSheet opened={open} onClose={close} title={title} sections={shown}>
            {children?.(close)}
          </ActionSheet>
        </span>
      </>
    );
  }

  return (
    <Menu opened={open} onChange={setOpen} position={position} width={width} shadow="md" withinPortal {...menuProps}>
      <Menu.Target>{target}</Menu.Target>
      <Menu.Dropdown onClick={stop} style={maxHeight ? { maxHeight, overflowY: 'auto' } : undefined}>
        {shown.map((section, si) => (
          <React.Fragment key={section.label ?? `section-${si}`}>
            {si > 0 && <Menu.Divider />}
            {section.label && <Menu.Label>{section.label}</Menu.Label>}
            {section.items.map((item, i) => (
              <Menu.Item
                key={`${item.label}-${i}`}
                color={item.color}
                disabled={item.disabled}
                leftSection={item.leftSection ?? (item.iconName ? <AppIcon iconName={item.iconName} size={16} /> : undefined)}
                rightSection={item.checked ? <AppIcon iconName="Check" size={14} /> : undefined}
                fz="md"
                onClick={item.onClick}
              >
                <Text span inherit ff={item.mono ? 'monospace' : undefined} fw={item.mono ? 600 : undefined}>
                  {item.label}
                </Text>
                {item.description && (
                  <Text span inherit c="dimmed">
                    {' '}
                    — {item.description}
                  </Text>
                )}
              </Menu.Item>
            ))}
          </React.Fragment>
        ))}
        {children?.(close)}
      </Menu.Dropdown>
    </Menu>
  );
};
